import React, { useCallback, useEffect, useState } from 'react';
import ViolationStateBlock from '../components/ViolationStateBlock';
import { getAnalyticsPolicy, updateAnalyticsPolicy } from '../../../services/analyticsPolicy';
import { describeHttpError } from '../../../utils/apiError';
import { ANALYTICS_EDIT_MODE_OPTIONS } from '../../../constants/analyticsConstants';

/**
 * Права на изменение правил и зон камеры (Part 4, раздел 6.9).
 *
 * Два режима независимы: камера может отдавать клиенту зоны и оставлять
 * правила за администратором. Сохранение идёт с expected_version, 409 —
 * повод перечитать политику, а не перезаписать чужое решение.
 *
 * Endpoint прав есть не на каждом backend. Если его нет, блок показывает это
 * прямым текстом: подставить mock и сказать «Права сохранены» нельзя — права
 * при этом не изменятся.
 */

const describePolicyError = (error) => {
  const status = error?.response?.status;
  if (status === 404 || status === 501) {
    return 'Управление правами пока недоступно в этой установке.';
  }
  return describeHttpError(error, 'Не удалось загрузить права на изменение');
};
const AnalyticsEditPolicySection = ({ cameraId, onPolicyLoaded }) => {
  const [policy, setPolicy] = useState(null);
  const [draft, setDraft] = useState({ rules: '', zones: '' });
  const [phase, setPhase] = useState('loading'); // loading | ready | error
  const [errorMessage, setErrorMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [savedAt, setSavedAt] = useState(0);

  const applyPolicy = useCallback((data) => {
    setPolicy(data);
    setDraft({ rules: data.rules_edit_mode, zones: data.zones_edit_mode });
    if (onPolicyLoaded) onPolicyLoaded(data);
  }, [onPolicyLoaded]);

  const loadPolicy = useCallback(() => {
    if (!cameraId) return Promise.resolve();
    setPhase('loading');

    return getAnalyticsPolicy(cameraId)
      .then((data) => {
        applyPolicy(data);
        setPhase('ready');
      })
      .catch((error) => {
        setPolicy(null);
        setErrorMessage(describePolicyError(error));
        setPhase('error');
      });
  }, [applyPolicy, cameraId]);

  useEffect(() => { loadPolicy(); }, [loadPolicy]);

  const save = () => {
    setSaving(true);
    setSaveError('');

    updateAnalyticsPolicy(cameraId, {
      expected_version: policy.version,
      rules_edit_mode: draft.rules,
      zones_edit_mode: draft.zones,
    })
      .then((updated) => {
        applyPolicy(updated);
        setSavedAt(Date.now());
      })
      .catch((error) => {
        if (error?.response?.status === 409) {
          setSaveError('Права изменил другой администратор. Показана актуальная версия.');
          return loadPolicy();
        }
        setSaveError(
          error?.response?.status === 404 || error?.response?.status === 501
            ? 'Backend не принял изменение прав: endpoint отсутствует. Права не изменены.'
            : describeHttpError(error, 'Не удалось сохранить права')
        );
        return undefined;
      })
      .finally(() => setSaving(false));
  };

  if (phase === 'loading') return <div className="vio-card"><ViolationStateBlock state="loading" /></div>;

  if (phase === 'error') {
    return (
      <div className="vio-card">
        <ViolationStateBlock state="error" message={errorMessage} onRetry={loadPolicy} />
      </div>
    );
  }

  if (!policy) return null;

  const dirty = draft.rules !== policy.rules_edit_mode || draft.zones !== policy.zones_edit_mode;

  return (
    <div className="vio-card">
      <p className="vio-drawer-section-title">Кто может изменять настройки камеры</p>

      <div className="vio-filters-grid">
        <div className="vio-field">
          <label className="vio-field-label" htmlFor="vio-policy-rules">Правила</label>
          <select
            id="vio-policy-rules"
            value={draft.rules}
            disabled={saving}
            onChange={(event) => setDraft((prev) => ({ ...prev, rules: event.target.value }))}
          >
            {ANALYTICS_EDIT_MODE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        <div className="vio-field">
          <label className="vio-field-label" htmlFor="vio-policy-zones">Зоны контроля</label>
          <select
            id="vio-policy-zones"
            value={draft.zones}
            disabled={saving}
            onChange={(event) => setDraft((prev) => ({ ...prev, zones: event.target.value }))}
          >
            {ANALYTICS_EDIT_MODE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      </div>

      {saveError && <p className="vio-field-error">{saveError}</p>}
      {savedAt > 0 && !dirty && !saveError && <p className="vio-field-hint">Права сохранены.</p>}

      <div className="vio-filters-actions">
        <button
          type="button"
          className="vio-btn vio-btn--primary"
          disabled={saving || !dirty}
          onClick={save}
        >
          {saving ? 'Сохраняем…' : 'Сохранить права'}
        </button>
      </div>
    </div>
  );
};

export default AnalyticsEditPolicySection;
